import Image from 'next/image';
import hero from '../assets/how-we-work/hero.jpg';
import { Button } from '../components/general/button';
import { A } from '../components/general/link';
import { BlueArch, WhiteArch } from '../components/img/svgs';
import { Meta } from '../components/meta/meta';

export default function HowWeWork() {
  return (
    <main>
      <Meta title="how we work" description="How families can receive furniture from Beds for Kids" noDefaultFooter />
      <section className="relative h-[70vh] w-full overflow-hidden">
        <Image
          alt="Child making his new bed"
          src={hero}
          className="absolute z-0 h-full w-full object-cover object-[50%_10%]"
          sizes="100vw"
          priority
        />
        <div className="pointer-events-none absolute -bottom-[1px] left-0 right-0">
          <WhiteArch />
        </div>
      </section>
      <section className="bg-white text-blue-500">
        <h1 className="mx-auto max-w-3xl py-4 text-center font-operetta text-6xl">how we work</h1>
        <div className="mx-auto max-w-3xl px-4 font-avenir text-xl leading-6">
          <p className="text-center font-caliban text-5xl leading-tight text-green-500">
            Are you in need of furniture?
          </p>
          <p className="mt-4 text-center">
            Please know that we would love to serve you and your family! In order to do that, you must meet our{' '}
            <A href="#qualifications" className="font-semibold underline hover:text-blue-400">
              qualifications for service
            </A>{' '}
            and receive a referral from a case or social worker with one of our referring partners. You can see
            participating agencies{' '}
            <A href="/agencies" className="font-semibold underline hover:text-blue-400">
              here
            </A>
            .
          </p>
        </div>
        <div className="mx-auto mt-8 flex max-w-xl flex-col gap-2 rounded-lg bg-blue-50 p-6 text-center font-avenir">
          <p className="text-sm italic">*Please note that there is a fee to receive furniture.</p>
          <p className="text-lg font-semibold">$60 for beds only</p>
          <p className="text-lg font-semibold">$100 for beds and additional furniture items</p>
        </div>
      </section>
      <Steps />
      <Qualifications />
      <BlueArch />
      <section className="bg-blue-500 pb-12 pt-4 text-center text-white">
        <div className="mx-auto max-w-3xl px-4">
          <h2 className="font-operetta text-5xl">ready to get started?</h2>
          <h6 className="mx-auto mt-8 max-w-lg font-avenir text-2xl font-medium">
            Connect with one of our partner agencies to begin the referral process.
          </h6>
          <Button className="mt-8" bType="secondary-white" link="/agencies">
            find a partner agency
          </Button>
        </div>
      </section>
    </main>
  );
}

const steps = [
  {
    title: 'Connect with a Partner Agency',
    body: 'If you already have a case manager at one of our partner agencies, ask them to submit a referral to Beds for Kids. If you’re not yet working with an agency, review our list of partner agencies, reach out to one that fits your needs, and establish services with a case manager.',
  },
  {
    title: 'Referral Review',
    body: 'Our Referral Partner Manager reviews the referral and adds you to our waiting list.',
  },
  {
    title: 'Wait for Contact',
    body: 'The waitlist changes frequently; we do our best to serve families as quickly as possible.',
  },
  {
    title: 'Schedule your Delivery',
    body: 'Once it’s your turn, our team will text you to schedule your delivery.',
  },
  {
    title: 'Delivery',
    body: 'On the scheduled date and time, a team will come to your home to deliver and assemble your furniture!',
  },
];

function Steps() {
  return (
    <section className="bg-white pb-12 pt-16 text-blue-500">
      <h2 className="mb-8 text-center font-operetta text-5xl">the process</h2>
      <div className="mx-auto flex max-w-3xl flex-col gap-6 px-4">
        {steps.map((step, i) => (
          <div
            key={step.title}
            className="flex flex-col gap-2 rounded-lg border border-green-500 p-6 text-center md:flex-row md:items-center md:gap-8 md:text-left">
            <div className="flex-shrink-0 font-caliban text-4xl text-green-500 md:w-40 md:text-center">
              Step {i + 1}
            </div>
            <div className="font-avenir">
              <h3 className="text-xl font-semibold">{step.title}</h3>
              <p className="mt-1 text-lg leading-6">{step.body}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

const qualifications = [
  'Must have at least 1 child (under 18 years old) in need of a bed',
  'Must live in Mecklenburg, Cabarrus, or Union County',
  'Cannot have been served by Beds for Kids previously; we are a one-time service',
];

function Qualifications() {
  return (
    <section className="bg-white pb-12 text-blue-500" id="qualifications">
      <div className="mx-auto max-w-3xl px-4">
        <h2 className="mb-8 text-center font-operetta text-5xl">qualifications for service</h2>
        <ul className="ml-[3px] flex flex-col gap-4 text-left font-avenir text-lg leading-6 md:gap-6">
          {qualifications.map(q => (
            <li key={q} className="border-l-[3px] border-green-500 py-1 pl-8">
              {q}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
